import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'
import vuetify from './plugins/vuetify'
import App from './App.vue'
import AdminRoutes from './admin/routes'
import { staffAuthGuard } from './admin/guards/staffAuthGuard'
import { onAuthErrorNavigation } from './admin/services/authNavigation'
import { useStaffAuthStore } from './admin/stores/staffAuthStore'
import {
  configureUsageTracking,
  trackUsagePage,
  installUsageActionTracking,
} from './admin/services/usageAnalytics'
import { initSentry } from './sentry'

import './style.css'
import 'leaflet/dist/leaflet.css'

// Create Pinia store
const pinia = createPinia()

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: (to) => ({ path: '/admin', query: to.query }),
    },
    ...AdminRoutes,
  ],
})

// Add navigation guard for authentication
router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresStaffAuth)) {
    return staffAuthGuard(to, from, next)
  }
  next()
})

onAuthErrorNavigation((errorCode) => {
  const currentRoute = router.currentRoute.value
  const currentErrorCode =
    typeof currentRoute.query.error === 'string' ? currentRoute.query.error : undefined

  if (currentRoute.name === 'AdminAuthError' && currentErrorCode === errorCode) {
    return
  }

  void router.replace({
    name: 'AdminAuthError',
    query: { error: errorCode },
  })
})

async function bootstrap(): Promise<void> {
  const app = createApp(App)

  app.use(pinia)
  app.use(vuetify)
  initSentry(app, router)
  app.use(router)

  // Usage analytics — wire after Pinia + Router so the staff auth store
  // and route metadata are available to the identity/page callbacks.
  configureUsageTracking({
    getUserId: () => useStaffAuthStore().session?.lytningUserId,
  })
  router.afterEach((to) => {
    trackUsagePage('page:' + String(to.name || to.path))
  })
  installUsageActionTracking(
    () => 'page:' + String(router.currentRoute.value.name || router.currentRoute.value.path)
  )

  await router.isReady()
  app.mount('#app')
}

bootstrap().catch((error) => {
  console.error('Failed to bootstrap admin application:', error)
})
